import { useEffect, useRef, useState } from 'react';

export const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible];
};

export const usePageSectionFadeIn = (pageKey, language) => {
  useEffect(() => {
    let observer;
    let timeoutId;

    const setup = () => {
      const sections = Array.from(document.querySelectorAll('main section'));
      if (sections.length === 0) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduceMotion) {
        sections.forEach(section => {
          section.dataset.sectionFade = 'true';
          section.dataset.sectionVisible = 'true';
        });
        return;
      }

      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.dataset.sectionVisible = 'true';
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.08, rootMargin: '0px 0px -5% 0px' }
      );

      sections.forEach((section, index) => {
        section.dataset.sectionFade = 'true';
        section.dataset.sectionVisible = 'false';
        section.style.transitionDelay = index === 0 ? '0.08s' : '';
      });

      requestAnimationFrame(() => {
        sections.forEach(section => observer.observe(section));
      });
    };

    // Wait two frames so the new page DOM is fully painted before we query sections
    timeoutId = setTimeout(() => requestAnimationFrame(setup), 50);

    return () => {
      clearTimeout(timeoutId);
      if (observer) {
        document.querySelectorAll('main section').forEach(section => {
          section.style.transitionDelay = '';
        });
        observer.disconnect();
      }
    };
  }, [pageKey, language]);
};

const FadeIn = ({ children, delay = 0, className = '', style = {} }) => {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export default FadeIn;
